
import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';
import { MOCK_BOOKINGS } from '../constants';
import { BookingStatus } from '../types';

const dataSales = [
  { name: 'Jan', v2023: 4000, v2024: 2400, v2025: 2400 },
  { name: 'Fev', v2023: 3000, v2024: 1398, v2025: 2210 },
  { name: 'Mar', v2023: 2000, v2024: 9800, v2025: 2290 },
  { name: 'Abr', v2023: 2780, v2024: 3908, v2025: 2000 },
  { name: 'Mai', v2023: 1890, v2024: 4800, v2025: 2181 },
  { name: 'Jun', v2023: 2390, v2024: 3800, v2025: 2500 },
  { name: 'Jul', v2023: 3490, v2024: 4300, v2025: 2100 },
];

const dataPax = [
  { name: 'Jan', Reservas: 40, PAX: 120 },
  { name: 'Fev', Reservas: 30, PAX: 98 },
  { name: 'Mar', Reservas: 20, PAX: 80 },
  { name: 'Abr', Reservas: 27, PAX: 75 },
  { name: 'Mai', Reservas: 18, PAX: 60 },
];

const Dashboard: React.FC = () => {
  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Painel de Controle</h1>
        <p className="text-slate-500 dark:text-slate-400">Indicadores-chave e visão geral de desempenho</p>
      </div>

      <div className="space-y-3 mb-10">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex items-center justify-between group hover:shadow-sm transition-all border-l-4 border-l-blue-500">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-500 rounded-lg">
              <span className="material-icons-round">schedule</span>
            </div>
            <div>
              <p className="text-sm font-semibold dark:text-white">Pagamento Pendente</p>
              <p className="text-xs text-slate-500">Pagamento de Margarete Menezes vence em breve</p>
            </div>
          </div>
          <p className="text-lg font-bold text-slate-900 dark:text-white">$30.600,00</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {[
          { label: 'Receita Total', value: '$100.610,01', trend: '+0.0%', icon: 'payments', color: 'emerald' },
          { label: 'Reservas Ativas', value: '28', trend: '-92.3%', icon: 'event_available', color: 'blue' },
          { label: 'Total de Clientes', value: '25', trend: '+2400.0%', icon: 'person_outline', color: 'orange' },
          { label: 'Total PAX (Ano)', value: '4', trend: '-97.2%', icon: 'group_add', color: 'indigo' },
        ].map((metric) => (
          <div key={metric.label} className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase">{metric.label}</p>
              <span className={`material-icons-round text-${metric.color}-500 text-lg`}>{metric.icon}</span>
            </div>
            <h3 className="text-2xl font-bold dark:text-white mb-1">{metric.value}</h3>
            <p className={`text-xs ${metric.trend.startsWith('+') ? 'text-emerald-500' : 'text-red-500'} flex items-center gap-1 font-medium`}>
              <span className="material-icons-round text-sm">{metric.trend.startsWith('+') ? 'north' : 'south'}</span>
              {metric.trend} vs ano anterior
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <h4 className="font-bold flex items-center gap-2 dark:text-white mb-6">
            <span className="material-icons-round text-primary">show_chart</span>
            Vendas Mensais (2023-2025)
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dataSales}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={12} stroke="#94a3b8" />
                <YAxis axisLine={false} tickLine={false} fontSize={12} stroke="#94a3b8" />
                <Tooltip />
                <Legend iconType="circle" />
                <Line type="monotone" dataKey="v2023" name="2023" stroke="#6366F1" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="v2024" name="2024" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="v2025" name="2025" stroke="#FBBF24" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <h4 className="font-bold flex items-center gap-2 dark:text-white mb-6">
            <span className="material-icons-round text-orange-400">people</span>
            Reservas vs Passageiros (PAX)
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dataPax}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={12} stroke="#94a3b8" />
                <YAxis axisLine={false} tickLine={false} fontSize={12} stroke="#94a3b8" />
                <Tooltip />
                <Legend iconType="square" />
                <Bar dataKey="Reservas" fill="#818CF8" radius={[4, 4, 0, 0]} barSize={20} />
                <Bar dataKey="PAX" fill="#34D399" radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h4 className="font-bold flex items-center gap-2 dark:text-white">
              <span className="material-icons-round text-primary">history</span>
              Reservas Recentes
            </h4>
            <button className="text-sm font-medium text-primary hover:underline">Ver todas</button>
          </div>
          <div className="space-y-4">
            {MOCK_BOOKINGS.map((booking) => (
              <div key={booking.id} className="flex items-center justify-between p-3 border border-slate-100 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-semibold text-sm dark:text-white">{booking.customerName}</span>
                    <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded uppercase">
                      {booking.status}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">{booking.pax} PAX</span>
                  </div>
                  <p className="text-xs text-slate-500">{booking.tour} • {booking.date}</p>
                </div>
                <p className="font-bold dark:text-white">{booking.currency} {booking.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
          <h4 className="font-bold flex items-center gap-2 dark:text-white mb-6">
            <span className="material-icons-round text-amber-500">bolt</span>
            Ações Rápidas
          </h4>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[
              { label: 'Ver Calendário', icon: 'calendar_today' },
              { label: 'Clientes', icon: 'group' },
              { label: 'Financeiro', icon: 'monetization_on' },
              { label: 'Tours', icon: 'map' },
            ].map((action) => (
              <button key={action.label} className="flex flex-col items-center justify-center p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all gap-2 group">
                <span className="material-icons-round text-slate-400 group-hover:text-primary transition-colors">{action.icon}</span>
                <span className="text-[11px] font-medium dark:text-slate-300">{action.label}</span>
              </button>
            ))}
          </div>
          <button className="mt-auto w-full bg-primary text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/20 active:scale-95">
            <span className="material-icons-round">add</span>
            Nova Reserva
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
